import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';
import { Products } from '@/common/const';
import { useHttpContext } from '@/common/hooks/HttpContext';
import { toasts } from '@/common/toasts';
import { BulkUpdateLocationItem, OrganizationAdmin, ScanModule } from '@/common/types';
import { a11yProps } from '@/common/utils';
import { AdminOrganizationLocations } from '@/components/Organization/AdminOrganization/AdminOrganizationLocations';
import { AdminOrganizationScanModules } from '@/components/Organization/AdminOrganization/AdminOrganizationScanModules';
import { LoadingSpinner } from '@/components/common/LoadingSpinner/LoadingSpinner';
import { PageTitle } from '@/components/common/PageTitle/PageTitle';
import { Button } from '@/components/common/button/button';
import { resultIsError } from '@/services/HttpService';
import { RouteContentWrapper } from '../../styles';
import { AdminOrganizationSettingsForm } from './AdminOrganizationSettingsForm';

const TAB_NAMES = ['settings', 'modules', 'locations'] as const;

export const AdminOrganizationRoute = () => {
    const { organizationId } = useParams<{ organizationId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const { httpService } = useHttpContext();
    const { errorToast, successToast } = toasts;
    const [isLoading, setIsLoading] = useState(false);
    const [organization, setOrganization] = useState<OrganizationAdmin | null>(null);
    const [isSavingLocations, setIsSavingLocations] = useState(false);
    const [pendingLocationUpdates, setPendingLocationUpdates] = useState<Map<number, boolean>>(new Map());

    const tabFromParam = TAB_NAMES.indexOf(searchParams.get('tab') as (typeof TAB_NAMES)[number]);
    const currentTab = tabFromParam === -1 ? 0 : tabFromParam;

    const handleTabChange = (_: SyntheticEvent, newValue: number) => {
        setSearchParams({ tab: TAB_NAMES[newValue] }, { replace: true });
    };

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            const res = await httpService.getOrganizationAdmin(parseInt(organizationId!));
            if (resultIsError(res)) {
                errorToast('Organization not found');
            } else {
                setOrganization(res);
            }
            setIsLoading(false);
        })();
    }, [organizationId, httpService, errorToast]);

    const submitScanModules = async (scanModules: ScanModule[]) => {
        if (!organization) {
            errorToast('No organization set.');
            return;
        }

        const res = await httpService.updateOrganizationAdmin(organization.id, { scanModules: scanModules });
        if (resultIsError(res)) {
            errorToast(res.message);
            return;
        }
        setOrganization(res);
        successToast(`${organization.name} Modules Updated`);
    };

    const handleApplyLocationUpdates = async () => {
        if (!organization) {
            errorToast('No organization set.');
            return;
        }

        const updates: BulkUpdateLocationItem[] = Array.from(
            pendingLocationUpdates,
            ([locationId, supportsParticipantDtls]) => ({
                locationId,
                supportsParticipantDtls,
            }),
        );

        setIsSavingLocations(true);
        const res = await httpService.bulkUpdateAdminScanLocationsForOrganization(organization.id, { updates });
        if (resultIsError(res)) {
            errorToast('Could not update locations.');
            setIsSavingLocations(false);
        } else {
            successToast('Locations updated.');
            window.location.reload();
        }
    };

    return (
        <RouteContentWrapper>
            {isLoading ? (
                <LoadingSpinner />
            ) : organization ? (
                <>
                    <Box
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            backgroundColor: 'grey.100',
                            mt: { xs: 0, md: -2 },
                            pt: { xs: 0, md: 2 },
                            borderBottom: '1px solid',
                            borderBottomColor: 'grey.300',
                        }}
                    >
                        <PageTitle title={organization.name} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Tabs value={currentTab} onChange={handleTabChange}>
                                <Tab label="Settings" {...a11yProps(0)} />
                                <Tab label="Scan Modules" {...a11yProps(1)} />
                                <Tab label="Locations" {...a11yProps(2)} />
                            </Tabs>
                            {currentTab === 2 && pendingLocationUpdates.size > 0 && (
                                <Button size="small" onClick={handleApplyLocationUpdates} disabled={isSavingLocations}>
                                    Apply Updates
                                </Button>
                            )}
                        </Box>
                    </Box>
                    <Box hidden={currentTab !== 0} sx={{ pt: 2 }}>
                        {currentTab === 0 && <AdminOrganizationSettingsForm organization={organization} />}
                    </Box>
                    <Box hidden={currentTab !== 1} sx={{ pt: 2 }}>
                        {organization.products.includes(Products.SCAN.toUpperCase()) ? (
                            <AdminOrganizationScanModules
                                organization={organization}
                                submitScanModules={submitScanModules}
                            />
                        ) : (
                            <Typography variant="h6" align="center">
                                Not a Scan Organization
                            </Typography>
                        )}
                    </Box>
                    <Box hidden={currentTab !== 2} sx={{ pt: 2 }}>
                        {currentTab === 2 && (
                            <AdminOrganizationLocations
                                organizationId={organization.id}
                                isSaving={isSavingLocations}
                                pendingUpdates={pendingLocationUpdates}
                                setPendingUpdates={setPendingLocationUpdates}
                            />
                        )}
                    </Box>
                </>
            ) : null}
        </RouteContentWrapper>
    );
};
